import { Modal, ModalProps, Radio, Typography } from 'antd';
import { AxiosResponse } from 'axios';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import usePrefetchPages from '../hooks/usePrefetchPages';
import { CandidateDetail } from '../types';
import Form from './Form';
import RichEditor from './RichEditor';
import EditorCharCounter from './RichEditor/EditorCharCounter';

interface ApplicantAnnotationFormValues {
  annotation: string;
  type: 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL';
}

interface ApplicantAnnotationModalProps extends ModalProps {
  candidateId: CandidateDetail['id'];
  fromStage: string | undefined;
  jobPostingId: number | undefined;
  onSuccess?: (
    data: AxiosResponse<void, unknown> | undefined | void,
    variables: ApplicantAnnotationFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const ApplicantAnnotationModal = ({
  candidateId,
  fromStage,
  jobPostingId,
  onSuccess,
  ...props
}: ApplicantAnnotationModalProps) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const annotationValue = Form.useWatch<string>('annotation', form);

  const mutation = useAPIMutation(
    async ({ annotation, type }: ApplicantAnnotationFormValues, api) => {
      await api.post('/candidate-commentary/', {
        commentary: annotation,
        type,
        candidate: candidateId,
        from_stage: fromStage,
        job_posting: jobPostingId,
      });
    },
    {
      onSuccess,
      onSettled: async () => {
        await queryClient.invalidateQueries('/candidate-history/');
      },
    }
  );

  const userPages = useInfiniteAPIQuery(['/users/', { params: { is_active: true } }]);

  const users = usePrefetchPages(userPages, (data) => data.results);

  return (
    <Modal
      okButtonProps={{
        htmlType: 'submit',
        loading: mutation.isLoading,
      }}
      okText="Salvar"
      onOk={() => form.submit()}
      title={<Typography.Text>Registrar comentário</Typography.Text>}
      {...props}
    >
      <Helmet>
        <title>Registrar comentário | Recrutamento e Seleção</title>
      </Helmet>
      <Form<ApplicantAnnotationFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          annotation: '',
          type: 'NEUTRAL',
        }}
        onFinish={async (variables) => {
          mutation.mutate(variables);
        }}
      >
        <Form.Item>
          <Form.Item
            noStyle
            label="Comentário"
            name="annotation"
            rules={[
              { required: true },
              {
                validator(_, value: string | undefined) {
                  if (!value || value?.replaceAll('<br>', '') === '<p></p>') {
                    return Promise.reject(new Error('Por favor, insira o comentário'));
                  }

                  if (value.length > 4000) {
                    return Promise.reject(
                      new Error(
                        `O comentário não pode ultrapassar 4000 caracteres. O contador de caracteres também contabiliza tags HTML (por exemplo, <p></p>) conforme a formatação escolhida`
                      )
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <RichEditor
              image={false}
              video={false}
              mentions={users.map(({ id, full_name }) => ({ id, value: full_name }))}
              data-testid="rich-editor-input"
            />
          </Form.Item>
          <EditorCharCounter count={annotationValue?.length || 0} limit={4000} />
        </Form.Item>
        <Form.Item label="Classificação" name="type">
          <Radio.Group buttonStyle="outline">
            <Radio.Button value="NEGATIVE">
              <Typography.Text type="danger">Negativo</Typography.Text>
            </Radio.Button>
            <Radio.Button value="NEUTRAL">
              <Typography.Text type="secondary">Neutro</Typography.Text>
            </Radio.Button>
            <Radio.Button value="POSITIVE">
              <Typography.Text type="success">Positivo</Typography.Text>
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ApplicantAnnotationModal;
